import type {Component} from 'typings/portaro.fe.types';
import type {
    ModalWindowActions,
    ModalWindowBindings,
    ModalWindowResolveValues
} from '../../../commons/conversation/conversation';
import type {IOnDestroy, IOnInit} from 'angular';
import {createPayloadActionResponse} from '../../../commons/conversation/modal-utils';
import type {Field007Code, Field007Definitions, Field007Label, Fond} from 'typings/portaro.be.types';
import {getCodesByPosition, getLabelsAndCodesByDocumentCategoryCode} from '../utils/utils';
import type {Field7Model} from '../utils/types';
import type {Field7EditorService} from './field7-editor.service';
import type {Field7EditorServiceFactory} from './field7-editor-service.factory';
import {ascendingOrderComparator} from 'shared/utils/array-utils.fp';
import type ModalConversationService from '../../../commons/conversation/modal-conversation.service';
import type {ILogService} from 'angular';
import {UnknownDocumentCategoryCodeError} from '../utils/field7-errors';
import KpUniversalForm from '../../../commons/value-editors/kp-universal-form/KpUniversalForm.svelte';
import type {KpUniversalFormSettings} from '../../../commons/value-editors/kp-universal-form/types';
import type {ObjectValueEditorField} from '../../../commons/value-editors/meta-editors/object/types';
import type {FormController} from '../../../commons/value-editors/internal/forms/types';
import type {Subscription} from 'rxjs';
import type {IScope} from 'angular';

export interface Field7EditorModalModel {
    value: string;
    fond: Fond;
    definitions: Field007Definitions;
}

export class Field7EditorModalComponent implements Component<ModalWindowBindings<Field7EditorModalModel>> {
    public static componentName = 'field7EditorModal';

    public bindings = {
        resolve: '<'
    } as const;
    public controller = Field7EditorModalComponentController;
    public templateUrl = require('./field7-editor-modal.component.tpl.ftl');
}

class Field7EditorModalComponentController implements ModalWindowBindings<Field7EditorModalModel>, IOnInit, IOnDestroy {
    public resolve: ModalWindowResolveValues<Field7EditorModalModel>;

    public kpUniversalFormComponent = KpUniversalForm;

    public modalWindowActions: ModalWindowActions;
    public model: Field7EditorModalModel;

    public formModel: Field7Model;
    public formSettings: KpUniversalFormSettings<Field7Model, ObjectValueEditorField<Field7Model>[]>; // form fields definition
    public isFormValid: boolean;

    private formController: FormController<Field7Model>;
    private subscription: Subscription;
    private editorService: Field7EditorService; // field 007 editor logic

    private definitions: Field007Definitions;
    private error: Error | null = null;

    private lastDocumentCategoryCode: string;

    /*@ngInject*/
    constructor(private field7EditorServiceFactory: Field7EditorServiceFactory,
                private modalConversationService: ModalConversationService,
                private $log: ILogService,
                private $scope: IScope) {
    }

    public async $onInit() {
        this.modalWindowActions = this.resolve.modalWindowActions;
        this.model = this.resolve.model;
        this.definitions = this.model.definitions;

        this.editorService = this.field7EditorServiceFactory.createService(this.definitions, this.$log);

        const allowedDocumentCategories = this.definitions.documentCategories
            .filter((documentType) => this.model.fond.documentCategories.includes(documentType.code))
            .sort(ascendingOrderComparator);

        if (allowedDocumentCategories.length === 0) {
            this.error = new Error('Žádné povolené kategorie dokumentu - změňte nastavení fondu');
            return; // show error and stop form initialization
        }

        this.formSettings = this.editorService.initializeFormsFirstSegment(allowedDocumentCategories); // initialize form

        try {
            this.formModel = this.editorService.parseCode(this.model.value);
        } catch (error: unknown) {
            this.$log.error(error);
            if (error instanceof UnknownDocumentCategoryCodeError) {
                const errorMessage = `pro kód kategorie dokumentu [${error.unknownCategoryCode}] nebyla nalezena definovaná hodnota`;
                await this.modalConversationService.confirmationDialog({text: `Pole 007 má chybnou strukturu (${errorMessage}). Chcete nastavit výchozí hodnoty?`})
            } else {
                throw new Error('Unknown field7 parsing error');
            }
        }

        // current value could be invalid because document category is not included in valid allowedDocumentCategories defined by the fond
        // or documentCategory could be undefined because value could not be parsed
        if (!allowedDocumentCategories.includes(this.formModel?.documentCategory)) { // empty or invalid field7 value
            this.formModel = {documentCategory: allowedDocumentCategories[0]}; // set first possible allowedDocumentCategories as default value
            this.updateFormAndResetModel(this.formModel.documentCategory.code);
        } else { // editing existing field7 value
            this.updateForm(this.formModel.documentCategory.code);
        }

        this.lastDocumentCategoryCode = this.formModel.documentCategory.code
    }

    public $onDestroy() {
        this.subscription?.unsubscribe();
    }

    public onChange() {
        if (this.formModel.documentCategory.code !== this.lastDocumentCategoryCode) { // changed document category
            this.updateFormAndResetModel(this.formModel.documentCategory.code); // reset form based on new document category
            this.lastDocumentCategoryCode = this.formModel.documentCategory.code; // save last document category
        }
    }

    public getFormController(formController: FormController<Field7Model>) {
        this.formController = formController;
        this.subscription = this.formController.getFieldState$().subscribe((state) => {
            this.isFormValid = state.valid;
            this.$scope.$applyAsync();
        });
    }

    public submit() {
        if (!this.isFormValid || this.hasError) {
            return;
        }

        this.modalWindowActions.submitPromise(Promise.resolve(createPayloadActionResponse(this.editorService.serializeModel(this.formModel))));
    }

    public get hasError(): boolean {
        return !!this.error;
    }

    public getError(): Error {
        return this.error;
    }

    private updateForm(code: string) {
        const [labels, codes] = getLabelsAndCodesByDocumentCategoryCode(this.definitions, code);
        this.formSettings = this.editorService.initializeFormsSecondSegment(this.formSettings, labels, codes); // change form according to document category code
    }

    private updateFormAndResetModel(code: string) {
        const [labels, codes] = getLabelsAndCodesByDocumentCategoryCode(this.definitions, code);
        this.formSettings = this.editorService.initializeFormsSecondSegment(this.formSettings, labels, codes); // change form according to document category code
        this.resetModel(labels, codes); // reset model - sets default values (all fields except document category)
    }

    private resetModel(labels: Field007Label[], codes: Field007Code[]) {
        this.formModel = {documentCategory: this.formModel.documentCategory};
        labels.forEach((label) => this.editorService.setPositionsDefaultValueToModel(this.formModel, label.order, getCodesByPosition(codes, label.order)))
    }
}